import React from 'react';
import { Link } from 'react-router-dom';
import { Mountain, ArrowLeft } from 'lucide-react';

const pf = { fontFamily: "'Playfair Display', Georgia, serif" };

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-stone-100 px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-stone-800 hover:text-[#2C5F8D] transition-colors">
            <Mountain className="w-5 h-5" />
            <span className="text-sm font-semibold tracking-tight" style={pf}>GoHimalayan</span>
          </Link>
          <Link to="/" className="flex items-center gap-1.5 text-stone-400 hover:text-stone-600 transition-colors text-xs">
            <ArrowLeft className="w-3.5 h-3.5" />Back to home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <p className="text-xs tracking-[0.3em] uppercase text-stone-400 mb-3">Legal</p>
        <h1 className="text-3xl sm:text-4xl font-light italic text-stone-900 mb-3" style={pf}>Privacy Policy</h1>
        <p className="text-stone-400 text-sm mb-12">Last updated: April 2026</p>

        <div className="prose prose-stone prose-sm max-w-none space-y-8">
          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-3" style={pf}>1. Who We Are</h2>
            <p className="text-stone-600 leading-relaxed">GoHimalayan ("we", "us", "our") operates the website gohimalayan.com and related services. We are a hospitality technology platform that curates and markets authentic homestays across the Indian Himalayas. Our registered address is New Delhi, India.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-3" style={pf}>2. Information We Collect</h2>
            <p className="text-stone-600 leading-relaxed mb-3">We collect information that you provide directly and information collected automatically when you use our platform:</p>
            <ul className="list-disc pl-5 space-y-2 text-stone-600">
              <li><strong>Contact information:</strong> Name, email address, phone number — when you submit a partner inquiry form or contact us.</li>
              <li><strong>Search queries:</strong> What you search for on our platform, to improve discovery and understand traveller demand patterns.</li>
              <li><strong>Wishlist data:</strong> Properties you save are stored locally in your browser (localStorage). We do not store this on our servers.</li>
              <li><strong>Usage data:</strong> Pages visited, time on site, device type, browser, approximate location — collected via analytics tools.</li>
              <li><strong>Host/partner data:</strong> Property details, images, descriptions, and contact information submitted by homestay owners through our partner onboarding flow.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-3" style={pf}>3. How We Use Your Information</h2>
            <ul className="list-disc pl-5 space-y-2 text-stone-600">
              <li>To operate, maintain, and improve the GoHimalayan platform.</li>
              <li>To connect travellers with homestay hosts via WhatsApp or other direct channels.</li>
              <li>To generate demand analytics for state tourism departments (aggregated, anonymised data only).</li>
              <li>To create and distribute marketing content (Instagram creatives, SEO pages) for partner properties.</li>
              <li>To respond to your inquiries and provide customer support.</li>
              <li>To comply with legal obligations.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-3" style={pf}>4. Data Sharing</h2>
            <p className="text-stone-600 leading-relaxed mb-3">We do not sell your personal data. We may share information with:</p>
            <ul className="list-disc pl-5 space-y-2 text-stone-600">
              <li><strong>Homestay hosts:</strong> When you initiate a booking inquiry via WhatsApp, your message is sent directly to the host.</li>
              <li><strong>State tourism departments:</strong> Aggregated, anonymised demand data (search trends, seasonal patterns) — never individual personal data.</li>
              <li><strong>Service providers:</strong> Analytics (PostHog), hosting infrastructure, and AI content generation services that process data on our behalf.</li>
              <li><strong>Legal requirements:</strong> When required by law, regulation, or legal process.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-3" style={pf}>5. Cookies & Analytics</h2>
            <p className="text-stone-600 leading-relaxed">We use essential cookies for site functionality and analytics cookies (PostHog) to understand how visitors use GoHimalayan. These help us improve the platform. You can disable cookies in your browser settings, though some features may not function optimally.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-3" style={pf}>6. Data Security</h2>
            <p className="text-stone-600 leading-relaxed">We implement industry-standard security measures to protect your data, including encrypted connections (HTTPS), secure database access, and regular security reviews. However, no method of transmission over the internet is 100% secure.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-3" style={pf}>7. Data Retention</h2>
            <p className="text-stone-600 leading-relaxed">We retain personal data only as long as necessary for the purposes described in this policy. Partner inquiry data is retained for the duration of the business relationship. Analytics data is retained in anonymised form. You may request deletion of your personal data at any time.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-3" style={pf}>8. Your Rights</h2>
            <p className="text-stone-600 leading-relaxed mb-3">You have the right to:</p>
            <ul className="list-disc pl-5 space-y-2 text-stone-600">
              <li>Access the personal data we hold about you.</li>
              <li>Request correction of inaccurate data.</li>
              <li>Request deletion of your personal data.</li>
              <li>Object to processing of your data for marketing purposes.</li>
              <li>Withdraw consent at any time where processing is based on consent.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-3" style={pf}>9. Third-Party Links</h2>
            <p className="text-stone-600 leading-relaxed">Our platform contains links to third-party services including WhatsApp, Instagram, and external property websites. We are not responsible for the privacy practices of these third-party services. We encourage you to review their privacy policies.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-3" style={pf}>10. Changes to This Policy</h2>
            <p className="text-stone-600 leading-relaxed">We may update this privacy policy from time to time. Changes will be posted on this page with an updated "Last updated" date. Continued use of GoHimalayan after changes constitutes acceptance of the updated policy.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-3" style={pf}>11. Contact Us</h2>
            <p className="text-stone-600 leading-relaxed">For any privacy-related questions or requests, contact us at:</p>
            <div className="mt-3 bg-stone-50 rounded-lg p-4 text-sm text-stone-600">
              <p className="font-semibold text-stone-800">GoHimalayan</p>
              <p>Email: privacy@gohimalayan.com</p>
              <p>WhatsApp: +91 9871155963</p>
              <p>Website: gohimalayan.com</p>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-stone-100 py-6 px-6 mt-16">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <span className="text-xs text-stone-400">© 2026 GoHimalayan</span>
          <Link to="/terms" className="text-xs text-stone-400 hover:text-stone-600 transition-colors">Terms of Service</Link>
        </div>
      </footer>
    </div>
  );
}
